import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { CheckCircle, AlertTriangle, CreditCard, ArrowLeft, Loader } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL + "/api";

const formatDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("fr-FR", { day: "2-digit", month: "long", year: "numeric" });
  } catch {
    return iso;
  }
};

export default function PractitionerBillingPage({ token }) {
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [flash, setFlash] = useState(null);

  const fetchStatus = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/practitioner/billing/status`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setStatus(res.data);
    } catch (err) {
      console.error(err);
      setFlash({ type: "error", text: "Impossible de charger votre abonnement." });
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (!token) { navigate("/practitioner/login"); return; }
    fetchStatus();
    // If redirected back from Stripe, poll until paid
    const params = new URLSearchParams(window.location.search);
    const statusParam = params.get("status");
    const sessionId = params.get("session_id");
    if (statusParam === "success" && sessionId) {
      setFlash({ type: "info", text: "Paiement en cours de validation…" });
      pollSession(sessionId, 0);
    } else if (statusParam === "cancel") {
      setFlash({ type: "warn", text: "Paiement annulé — vous pouvez réessayer quand vous voulez." });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const pollSession = async (sessionId, attempts) => {
    if (attempts >= 8) return; // ~16s max
    try {
      const res = await axios.get(`${API_URL}/practitioner/billing/session/${sessionId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.data.payment_status === "paid") {
        setFlash({ type: "success", text: "Paiement validé ! Votre abonnement est actif." });
        fetchStatus();
        return;
      }
    } catch (err) {
      // keep polling
    }
    setTimeout(() => pollSession(sessionId, attempts + 1), 2000);
  };

  const subscribe = async (plan) => {
    setSubmitting(true);
    try {
      const res = await axios.post(
        `${API_URL}/practitioner/billing/checkout`,
        { plan, origin_url: window.location.origin },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      window.location.href = res.data.url;
    } catch (err) {
      setFlash({ type: "error", text: err.response?.data?.detail || "Erreur lors de la création du paiement." });
      setSubmitting(false);
    }
  };

  const openPortal = async () => {
    setSubmitting(true);
    try {
      const res = await axios.post(
        `${API_URL}/practitioner/billing/portal`,
        { origin_url: window.location.origin },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      window.location.href = res.data.url;
    } catch (err) {
      setFlash({ type: "error", text: err.response?.data?.detail || "Erreur d'accès au portail." });
      setSubmitting(false);
    }
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center">
      <Loader className="w-8 h-8 animate-spin text-emerald-600" />
    </div>
  );

  const isActive = status?.status === "active";
  const isExempted = status?.status === "exempted";
  const isPastDue = status?.status === "past_due";
  const isSuspended = status?.status === "suspended";
  const isTrial = status?.status === "trial";
  const isCancelled = status?.status === "cancelled";

  return (
    <div className="min-h-screen bg-slate-50 py-10 px-4">
      <div className="max-w-3xl mx-auto">
        <button
          onClick={() => navigate("/practitioner/dashboard")}
          className="flex items-center gap-2 text-slate-600 hover:text-emerald-600 mb-6 text-sm"
          data-testid="billing-back-btn"
        >
          <ArrowLeft className="w-4 h-4" /> Retour au tableau de bord
        </button>

        <h1 className="text-3xl font-bold text-slate-900 mb-2">Abonnement DoctoDays</h1>
        <p className="text-slate-600 mb-8">Gérez votre abonnement praticien et vos informations de facturation.</p>

        {flash && (
          <div
            className={`mb-6 p-4 rounded-lg border ${
              flash.type === "success" ? "bg-emerald-50 border-emerald-200 text-emerald-800"
                : flash.type === "error" ? "bg-red-50 border-red-200 text-red-800"
                : flash.type === "warn" ? "bg-amber-50 border-amber-200 text-amber-800"
                : "bg-blue-50 border-blue-200 text-blue-800"
            }`}
            data-testid="billing-flash"
          >
            {flash.text}
          </div>
        )}

        {/* EXEMPTED */}
        {isExempted && (
          <div className="bg-white rounded-2xl shadow-sm border border-emerald-100 p-8" data-testid="billing-card-exempted">
            <div className="flex items-center gap-3 mb-3">
              <CheckCircle className="w-7 h-7 text-emerald-600" />
              <h2 className="text-xl font-semibold text-slate-900">Accès gratuit</h2>
            </div>
            <p className="text-slate-600">
              Votre compte bénéficie d'un accès gratuit à DoctoDays{status.exempted_reason ? ` (${status.exempted_reason})` : ""}.
              Aucun paiement n'est requis.
            </p>
          </div>
        )}

        {/* ACTIVE */}
        {isActive && (
          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-8 space-y-6" data-testid="billing-card-active">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <CheckCircle className="w-7 h-7 text-emerald-600" />
                <h2 className="text-xl font-semibold text-slate-900">Abonnement actif</h2>
              </div>
              <p className="text-slate-600">
                Plan <strong>{status.plan === "yearly" ? "Annuel (395€/an)" : "Mensuel (39€/mois)"}</strong> —
                prochain prélèvement le <strong>{formatDate(status.current_period_end)}</strong>.
              </p>
            </div>
            <button
              onClick={openPortal}
              disabled={submitting}
              className="px-5 py-3 bg-slate-900 text-white rounded-lg hover:bg-slate-800 font-medium inline-flex items-center gap-2 disabled:opacity-50"
              data-testid="billing-portal-btn"
            >
              <CreditCard className="w-4 h-4" /> Gérer mon abonnement et mes factures
            </button>
          </div>
        )}

        {/* PAST DUE / SUSPENDED / CANCELLED → offer subscription */}
        {(isPastDue || isSuspended || isCancelled || isTrial) && (
          <>
            {(isPastDue || isSuspended) && (
              <div className="mb-6 p-5 rounded-2xl bg-red-50 border border-red-200" data-testid="billing-alert">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <h3 className="font-semibold text-red-900 mb-1">
                      {isSuspended ? "Votre profil est actuellement masqué" : "Action requise — délai de grâce"}
                    </h3>
                    <p className="text-sm text-red-800">
                      {isSuspended
                        ? "Votre profil n'apparaît plus dans les résultats de recherche. Souscrivez pour le réactiver immédiatement."
                        : `Il vous reste ${status.days_left_grace ?? 0} jour(s) pour souscrire avant que votre profil ne soit masqué des résultats.`}
                    </p>
                  </div>
                </div>
              </div>
            )}

            <div className="grid md:grid-cols-2 gap-4">
              <PlanCard
                title="Mensuel"
                price={`${status.monthly_price_eur} €`}
                period="par mois"
                description="Sans engagement, annulable à tout moment."
                onSubscribe={() => subscribe("monthly")}
                submitting={submitting}
                testId="billing-plan-monthly"
              />
              <PlanCard
                title="Annuel"
                price={`${status.yearly_price_eur} €`}
                period="par an"
                description="Économisez 15 % — équivalent 33 €/mois."
                featured
                onSubscribe={() => subscribe("yearly")}
                submitting={submitting}
                testId="billing-plan-yearly"
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function PlanCard({ title, price, period, description, onSubscribe, submitting, featured, testId }) {
  return (
    <div
      className={`bg-white rounded-2xl shadow-sm border p-6 ${featured ? "border-emerald-500 ring-2 ring-emerald-500/20" : "border-slate-200"}`}
      data-testid={testId}
    >
      {featured && (
        <div className="inline-block mb-2 px-2 py-0.5 text-xs font-semibold rounded-full bg-emerald-600 text-white">
          Recommandé
        </div>
      )}
      <h3 className="text-lg font-semibold text-slate-900 mb-1">{title}</h3>
      <div className="flex items-baseline gap-1 mb-3">
        <span className="text-3xl font-bold text-slate-900">{price}</span>
        <span className="text-slate-500 text-sm">{period}</span>
      </div>
      <p className="text-sm text-slate-600 mb-5">{description}</p>
      <button
        onClick={onSubscribe}
        disabled={submitting}
        className={`w-full py-3 rounded-lg font-medium transition-colors disabled:opacity-50 ${
          featured ? "bg-emerald-600 text-white hover:bg-emerald-700" : "bg-slate-900 text-white hover:bg-slate-800"
        }`}
        data-testid={`${testId}-subscribe`}
      >
        {submitting ? "Redirection…" : "Souscrire"}
      </button>
    </div>
  );
}
